import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react'

export interface Breadcrumb {
  label: string
  path: string
}

interface BreadcrumbsContextType {
  breadcrumbs: Breadcrumb[]
  push: (crumb: Breadcrumb) => () => void
}

const BreadcrumbsContext = createContext<BreadcrumbsContextType>(undefined!)

export function BreadcrumbsProvider({ children }: PropsWithChildren) {
  // State
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([])

  // Push a new breadcrumb and return a function to remove it
  // This is useful for adding breadcrumbs in components that unmount
  // and want to clean up after themselves
  const push = useCallback((crumb: Breadcrumb) => {
    setBreadcrumbs((prev) => [...prev, crumb])
    return () => setBreadcrumbs((prev) => prev.filter((c) => c !== crumb))
  }, [])

  // Context value
  const value: BreadcrumbsContextType = {
    breadcrumbs,
    push,
  }

  return <BreadcrumbsContext value={value}>{children}</BreadcrumbsContext>
}

export function useBreadcrumbs() {
  const context = useContext(BreadcrumbsContext)
  if (context === undefined) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbsProvider')
  }

  return context
}
